import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { TextField } from '@radix-ui/themes'
import { NavigateFn, useSearch } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import React from 'react'
import styled from 'styled-components'

import { ShipFilterBar } from './filters/ShipFilterBar'

const StyledFilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
  gap: 1rem;
`

const InlineFilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex: 1;
  min-width: 0;
`

const StyledTextFieldRoot = styled(TextField.Root)`
  min-height: 2rem;
  min-width: 200px;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton} !important;
  }
`

const StyledTextFieldSlot = styled(TextField.Slot)`
  padding: 4px;
`

export const NavyShipTableTopBar = <TData,>({
  columns,
  setGlobalFilter,
  getColumn,
  initialColumnFilters,
  columnFilters,
  navigate,
  disableUrlFilters = false,
}: {
  columns: ColumnDef<TData>[]
  setGlobalFilter: React.Dispatch<unknown>
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  initialColumnFilters?: ColumnFiltersState
  // define which columns are filterable
  columnFilters?: string[]
  navigate: NavigateFn
  disableUrlFilters?: boolean
}) => {
  const search = useSearch({ strict: false })
  const searchParam = search.search as string
  const [searchValue, setSearchValue] = React.useState(searchParam || '')

  // Sync with URL on mount and URL changes
  React.useEffect(() => {
    setSearchValue(searchParam || '')
    setGlobalFilter(searchParam || '')
  }, [searchParam, setGlobalFilter])

  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    setSearchValue(value)
    setGlobalFilter(value)
    if (!disableUrlFilters) {
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          search: value || undefined,
        }),
      })
    }
  }

  return (
    <StyledFilterContainer>
      <InlineFilterGroup>
        <ShipFilterBar
          columns={columns}
          getColumn={getColumn}
          columnFilters={columnFilters}
          initialColumnFilters={initialColumnFilters}
          navigate={navigate}
          disableUrlFilters={disableUrlFilters}
        />
        <StyledTextFieldRoot
          placeholder='Search'
          variant='soft'
          value={searchValue}
          onChange={handleFilterInputChange}>
          <StyledTextFieldSlot>
            <MagnifyingGlassIcon height='16' width='16' />
          </StyledTextFieldSlot>
        </StyledTextFieldRoot>
      </InlineFilterGroup>
    </StyledFilterContainer>
  )
}
